// @boty/database - Intake Questions Query Utilities (Story 7.6)

import type { SupabaseClient } from '@supabase/supabase-js';
import type { Database, IntakeQuestionInsert } from '../types';

type DbClient = SupabaseClient<Database>;

// ============================================================================
// INTAKE QUESTIONS QUERIES
// ============================================================================

/**
 * Get all intake questions for a bot, sorted by sort_order.
 */
export async function getIntakeQuestions(client: DbClient, botId: string) {
  const { data, error } = await client
    .from('intake_questions')
    .select('*')
    .eq('bot_id', botId)
    .order('sort_order', { ascending: true });

  if (error) throw error;
  return data;
}

/**
 * Create a new intake question for a bot.
 */
export async function createIntakeQuestion(
  client: DbClient,
  data: IntakeQuestionInsert,
) {
  const { data: result, error } = await client
    .from('intake_questions')
    .insert(data)
    .select()
    .single();

  if (error) throw error;
  return result;
}

/**
 * Update a single intake question.
 */
export async function updateIntakeQuestion(
  client: DbClient,
  questionId: string,
  botId: string,
  updates: { question_text?: string; sort_order?: number },
) {
  const { data, error } = await client
    .from('intake_questions')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', questionId)
    .eq('bot_id', botId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete an intake question (scoped to bot for defense in depth).
 */
export async function deleteIntakeQuestion(
  client: DbClient,
  questionId: string,
  botId: string,
) {
  const { error } = await client
    .from('intake_questions')
    .delete()
    .eq('id', questionId)
    .eq('bot_id', botId);

  if (error) throw error;
}

/**
 * Bulk update intake questions (for reorder and text changes).
 * Accepts array of { id, question_text, sort_order }.
 */
export async function bulkUpdateIntakeQuestions(
  client: DbClient,
  botId: string,
  questions: { id: string; question_text: string; sort_order: number }[],
) {
  const now = new Date().toISOString();
  const updates = questions.map((q) =>
    client
      .from('intake_questions')
      .update({
        question_text: q.question_text,
        sort_order: q.sort_order,
        updated_at: now,
      })
      .eq('id', q.id)
      .eq('bot_id', botId)
  );

  const results = await Promise.all(updates);
  const errors = results.filter((r) => r.error);
  if (errors.length > 0) throw errors[0]!.error;

  return { count: questions.length };
}

/**
 * Count intake questions for a bot (to enforce max 10 limit).
 */
export async function countIntakeQuestions(
  client: DbClient,
  botId: string,
): Promise<number> {
  const { count, error } = await client
    .from('intake_questions')
    .select('*', { count: 'exact', head: true })
    .eq('bot_id', botId);

  if (error) throw error;
  return count || 0;
}

/**
 * Get the next sort_order value for a new question.
 */
export async function getNextIntakeQuestionOrder(
  client: DbClient,
  botId: string,
): Promise<number> {
  const { data, error } = await client
    .from('intake_questions')
    .select('sort_order')
    .eq('bot_id', botId)
    .order('sort_order', { ascending: false })
    .limit(1);

  if (error) throw error;
  return data && data.length > 0 ? data[0]!.sort_order + 1 : 0;
}
